import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import DatePicker from "react-datepicker";
import {
  fetchStockTransfers,
  selectStockTransfers,
  selectStockTransfersError,
  selectStockTransfersStatus,
} from "../features/stockTransfers/stockTransfersSlice";
import "./StockTransfersPage.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function stockTransferStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "draft") return "Черновик";
  if (s === "submitted") return "Отправлено";
  if (s === "received") return "Получено";
  if (s === "cancelled") return "Отменено";
  return status || "—";
}

function statusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "draft") return "var(--c-text-sub)";
  if (s === "submitted") return "var(--c-accent)";
  if (s === "received") return "var(--c-success)";
  if (s === "cancelled") return "var(--c-danger)";
  return "var(--c-text-sub)";
}

export default function StockTransfersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const PAGE_SIZE = 25;

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [status, setStatus] = useState(() => (searchParams.get("status") ?? "draft"));
  const [createdFrom, setCreatedFrom] = useState(() => {
    const raw = searchParams.get("created_from");
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  });
  const [createdTo, setCreatedTo] = useState(() => {
    const raw = searchParams.get("created_to");
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(() => {
    const raw = searchParams.get("page");
    const n = Number(raw || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });

  const rows = useSelector(selectStockTransfers);
  const loadStatus = useSelector(selectStockTransfersStatus);
  const error = useSelector(selectStockTransfersError);

  const searchTrimmed = useMemo(() => search.trim(), [search]);
  const statusTrimmed = useMemo(() => status.trim(), [status]);
  const createdFromIso = useMemo(() => (createdFrom instanceof Date && !Number.isNaN(createdFrom.getTime()) ? createdFrom.toISOString() : null), [createdFrom]);
  const createdToIso = useMemo(() => (createdTo instanceof Date && !Number.isNaN(createdTo.getTime()) ? createdTo.toISOString() : null), [createdTo]);

  // Keep URL in sync, so Back/Reload preserves pagination/filters
  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTrimmed) next.set("search", searchTrimmed);
    // default status is "draft" - include only when differs (or explicitly empty for "all")
    if (String(statusTrimmed) !== "draft") next.set("status", String(statusTrimmed));
    if (createdFromIso) next.set("created_from", createdFromIso);
    if (createdToIso) next.set("created_to", createdToIso);
    if (page && page !== 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [createdFromIso, createdToIso, page, searchTrimmed, setSearchParams, statusTrimmed]);

  // Hydrate local state from URL (Back/Forward)
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlStatus = searchParams.get("status") ?? "draft";
    const urlFromRaw = searchParams.get("created_from");
    const urlToRaw = searchParams.get("created_to");
    const urlPageRaw = searchParams.get("page");
    const urlPageNum = Number(urlPageRaw || 1);
    const urlPage = Number.isFinite(urlPageNum) && urlPageNum > 0 ? Math.floor(urlPageNum) : 1;

    const urlFrom = (() => {
      if (!urlFromRaw) return null;
      const d = new Date(urlFromRaw);
      return Number.isNaN(d.getTime()) ? null : d;
    })();
    const urlTo = (() => {
      if (!urlToRaw) return null;
      const d = new Date(urlToRaw);
      return Number.isNaN(d.getTime()) ? null : d;
    })();

    if (urlSearch !== search) setSearch(urlSearch);
    if (urlStatus !== status) setStatus(urlStatus);
    if ((urlFromRaw || "") !== (createdFromIso || "")) setCreatedFrom(urlFrom);
    if ((urlToRaw || "") !== (createdToIso || "")) setCreatedTo(urlTo);
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchStockTransfers({ search: searchTrimmed, status: statusTrimmed, createdFrom: createdFromIso, createdTo: createdToIso }));
  }, [dispatch, refreshKey, searchTrimmed, statusTrimmed, createdFromIso, createdToIso]);

  const totalCount = Array.isArray(rows) ? rows.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    // Clamp only after data is loaded to avoid resetting page on hard reload.
    if (loadStatus === "succeeded" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  const visibleRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const start = (page - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [PAGE_SIZE, page, rows]);

  return (
    <div className="stock-transfers-page">
      <div className="header">
        <div>
          <div className="title">Перемещения по складам</div>
          <div className="sub-title">
            Список перемещений (данные из <span className="mono">/api/product/stock-transfers/</span>).
          </div>
        </div>

        <div className="header-actions">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск: id / name / магазины / комментарий / товар…"
            className="search"
          />
          <Select
            value={status}
            onChange={(v) => { setStatus(v);
              setPage(1); }}
            options={[
              { value: "", label: "Все статусы" },
              { value: "draft", label: stockTransferStatusLabel("draft") },
              { value: "submitted", label: stockTransferStatusLabel("submitted") },
              { value: "received", label: stockTransferStatusLabel("received") },
              { value: "cancelled", label: stockTransferStatusLabel("cancelled") },
            ]}
            isSearchable={false}
          />
          <div className="date-group">
            <div className="date-label">Дата создания</div>
            <div className="date-row">
              <DatePicker
                selected={createdFrom}
                onChange={(d) => {
                  setCreatedFrom(d);
                  setPage(1);
                }}
                dateFormat="dd.MM.yyyy"
                placeholderText="c"
                isClearable
                customInput={<input className="date-input" />}
              />
              <DatePicker
                selected={createdTo}
                onChange={(d) => {
                  setCreatedTo(d);
                  setPage(1);
                }}
                dateFormat="dd.MM.yyyy"
                placeholderText="по"
                isClearable
                customInput={<input className="date-input" />}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              navigate("/warehouse/transfers/new", {
                state: { from: `${location.pathname}${location.search}` },
              })
            }
            className="btn-primary"
          >
            + Добавить перемещение
          </button>
          <button type="button" onClick={() => setRefreshKey((v) => v + 1)} className="btn">
            Обновить
          </button>
          {(createdFrom || createdTo) && (
            <button
              type="button"
              onClick={() => {
                setCreatedFrom(null);
                setCreatedTo(null);
                setPage(1);
              }}
              className="btn-ghost"
              title="Сбросить фильтр по дате"
            >
              Сброс даты
            </button>
          )}
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div className="error">{error}</div>}

      <div className="body">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="th">ID</th>
                <th className="th">Название</th>
                <th className="th">Откуда</th>
                <th className="th">Куда</th>
                <th className="th">Статус</th>
                <th className="th">Позиций</th>
                <th className="th">Сумма (опт)</th>
                <th className="th">Отправлено</th>
                <th className="th">Получено</th>
                <th className="th">Создано</th>
                <th className="th">Обновлено</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && loadStatus === "succeeded" ? (
                <tr>
                  <td colSpan={11} className="td-muted">
                    Ничего не найдено
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    className="tr"
                    onClick={() =>
                      navigate(`/warehouse/transfers/${r.id}`, {
                        state: { from: `${location.pathname}${location.search}` },
                      })
                    }
                    title="Открыть перемещение"
                  >
                    <td className="td-mono">{r.id}</td>
                    <td className="td">{r.name || "—"}</td>
                    <td className="td">{r.from_store_name || `Store #${r.from_store}` || "—"}</td>
                    <td className="td">{r.to_store_name || `Store #${r.to_store}` || "—"}</td>
                    <td className="td">
                      <span className="badge" style={{ borderColor: statusColor(r.status), color: statusColor(r.status) }}>
                        {stockTransferStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="td">{typeof r.items_count === "number" ? r.items_count : "—"}</td>
                    <td className="td">{formatMoney(r.wholesale_total)}</td>
                    <td className="td">{formatDateTime(r.submitted_at)}</td>
                    <td className="td">{formatDateTime(r.received_at)}</td>
                    <td className="td">{formatDateTime(r.created_at)}</td>
                    <td className="td">{formatDateTime(r.updated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <PaginationBar
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            itemsOnPage={visibleRows.length}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}

